import React from "react";
import { useEffect } from "react";
import { useMemo } from "react";
import { useState } from "react";

import Button from "@mui/material/Button";
import LoadingButton from "@mui/lab/LoadingButton";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

import ConfiguredForm from "../../UtilityComponents/FormUtilityComponents/ConfiguredForm";
import FormConfig from "../../UtilityComponents/FormUtilityComponents/FormConfig";
import FormRowState from "../../UtilityComponents/FormUtilityComponents/FormRowState";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";

import { useTheme } from "@mui/material/styles";

import FormIsValid from "../../UtilityComponents/FormUtilityComponents/FormIsValid";
import { DialogContentText } from "@mui/material";

type ScenarioDialogProps = {
  open: boolean;
  loading: boolean;
  dialogTitle: string;
  description: string;
  config: FormConfig;
  handleCancel: () => void;
  isNew: boolean;
  isPrimary: boolean;
  handleAddOrSave: (state: FormRowState[]) => void;
  rowStateList: FormRowState[];
  setRowStateList: (newState: FormRowState[] | ((oldState: FormRowState[]) => FormRowState[])) => void;
  disallowedNames: string[];
  handleDelete: () => void;
  completeLoadingOnDialogClose: boolean;
  setCompleteLoadingOnDialogClose: (value: boolean) => void;
  setLoading: (value: boolean) => void;
};

function ScenarioDialog(props: ScenarioDialogProps) {
  const theme = useTheme();
  const [deleteOrAddSaveLastClicked, setDeleteOrAddSaveLastClicked] = useState<"delete" | "add/save" | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const candidateScenarioName =
    props.rowStateList && props.rowStateList.length > 0
      ? (props.rowStateList[0].currentModeStates[props.rowStateList[0].currentModeIndex] as string)
      : "";

  const loading = props.loading;
  const nameCollision = useMemo(
    () => props.disallowedNames.includes(candidateScenarioName) && !loading,
    [props.disallowedNames, candidateScenarioName, loading]
  );

  const setRowStateList = props.setRowStateList;

  useEffect(() => {
    setRowStateList((oldState) => {
      var newState = oldState;
      if (oldState[0].currentModeErrors === undefined) {
        newState[0].currentModeErrors = oldState[0].currentModeStates.map((_, index) =>
          index === 0 && nameCollision ? "You already have a forecast with this name" : null
        );
      } else {
        newState[0].currentModeErrors = oldState[0].currentModeErrors.map((oldError, index) =>
          index === 0 ? (nameCollision ? "You already have a forecast with this name" : null) : oldError
        );
      }
      return newState;
    });
  }, [nameCollision, setRowStateList]);

  function submitClick() {
    setDeleteOrAddSaveLastClicked("add/save");
    props.handleAddOrSave(props.rowStateList);
  }

  return (
    <Dialog
      open={props.open}
      onClose={() => {
        // dont allow unexplicit click away if loading
        if (!props.loading && !refreshing) {
          props.handleCancel();
        }
      }}
      maxWidth="md"
      fullWidth
      TransitionProps={{
        ...(theme?.components?.MuiDialog?.defaultProps?.TransitionProps || {}),
        onExited: () => {
          if (props.completeLoadingOnDialogClose) {
            props.setLoading(false);
            props.setCompleteLoadingOnDialogClose(false);
          }
        },
      }}
    >
      <DialogTitle>{props.dialogTitle}</DialogTitle>
      <DialogContent>
        <ConfiguredForm
          config={props.config}
          refreshing={refreshing}
          setRefreshing={setRefreshing}
          state={props.rowStateList}
          setState={(item) => {
            props.setRowStateList(item);
          }}
          canSubmit={FormIsValid(props.rowStateList, props.config) && !props.loading && !refreshing && !nameCollision}
          handleSubmit={submitClick}
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Button color="error" startIcon={<CloseIcon />} onClick={props.handleCancel} disabled={refreshing}>
          {/* allows cancel even if loading, but not if refreshing */}
          Cancel
        </Button>
        {props.isNew ? null : (
          <LoadingButton
            variant="contained"
            loading={deleteOrAddSaveLastClicked === "delete" && props.loading}
            disabled={props.isNew || (deleteOrAddSaveLastClicked === "add/save" && props.loading && refreshing)}
            color={props.isPrimary ? "success" : "error"}
            startIcon={props.isPrimary ? <DoneAllOutlinedIcon /> : <DeleteIcon />}
            onClick={() => {
              if (props.isPrimary) {
                return;
              }
              setDeleteOrAddSaveLastClicked("delete");
              props.handleDelete();
            }}
          >
            {props.isPrimary ? "Locked In" : "Delete"}
          </LoadingButton>
        )}
        <LoadingButton
          variant="contained"
          loading={deleteOrAddSaveLastClicked === "add/save" && props.loading}
          disabled={
            !FormIsValid(props.rowStateList, props.config) ||
            nameCollision ||
            (deleteOrAddSaveLastClicked === "delete" && props.loading && refreshing)
          }
          startIcon={props.isNew ? <AddIcon /> : <SaveIcon />}
          onClick={submitClick}
        >
          {props.isNew ? "Add" : "Save"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default ScenarioDialog;
